import type { ImportDesignerPreview, ImportDesignerPreviewRow } from '@today/types'
import { cn } from '@today/ui-primitives'

/**
 * Props for {@link PreviewTable}.
 *
 * @property preview - The preview response.
 * @property columns - The canonical fields to show, in order.
 */
export interface PreviewTableProps {
	preview: ImportDesignerPreview
	columns: readonly string[]
}

const cell = (value: unknown) => (value === null || value === undefined ? '—' : String(value))

const errorText = (row: ImportDesignerPreviewRow) =>
	row.errors
		.map((e) => {
			const field = e.targetField ?? e.sourceField
			return `${e.stage} ${e.code}${field ? ` (${field})` : ''}`
		})
		.join('; ')

function PreviewRow({ row, columns }: { row: ImportDesignerPreviewRow; columns: readonly string[] }) {
	const failed = row.errors.length > 0
	return (
		<tr
			aria-invalid={failed || undefined}
			data-row-error={failed || undefined}
			className={cn(failed && 'bg-ds-surface-muted text-ds-danger')}
		>
			<th scope="row">{row.recordNumber}</th>
			{columns.map((c) => (
				<td key={c}>{row.values ? cell(row.values[c]) : '—'}</td>
			))}
			<td>{errorText(row)}</td>
		</tr>
	)
}

function PreviewSummary({ preview }: { preview: ImportDesignerPreview }) {
	const { totals } = preview
	return (
		<>
			<p role="status">
				{preview.outcome} — {totals.records} records, {totals.acceptedRecords} accepted, {totals.rejectedRecords}{' '}
				rejected
				{preview.failedStage ? ` · failed at ${preview.failedStage}` : ''}
			</p>
			{preview.fileErrors.length > 0 && (
				<ul aria-label="File errors" className="text-ds-danger">
					{preview.fileErrors.map((e, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: errors are ordered and may repeat
						<li key={i}>
							{e.stage} · {e.code}
						</li>
					))}
				</ul>
			)}
		</>
	)
}

/**
 * The first N records as the engine produced them (TO-400 SCN-004): canonical
 * values for an accepted record, its errors for a rejected one, plus the
 * outcome, totals and file-level errors. Pure.
 */
export function PreviewTable({ preview, columns }: PreviewTableProps) {
	return (
		<div className="flex flex-col gap-2 text-ds-sm text-ds-text">
			<PreviewSummary preview={preview} />
			<table className="w-full text-left">
				<caption className="sr-only">Previewed records</caption>
				<thead>
					<tr>
						<th scope="col">#</th>
						{columns.map((c) => (
							<th key={c} scope="col">
								{c}
							</th>
						))}
						<th scope="col">Errors</th>
					</tr>
				</thead>
				<tbody>
					{preview.rows.map((row) => (
						<PreviewRow key={row.recordNumber} row={row} columns={columns} />
					))}
				</tbody>
			</table>
		</div>
	)
}
